import React, { useEffect, useState } from "react";
import { UserCog, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import Icd10Autocomplete from "@/components/Icd10Autocomplete";

export default function PeerConsult() {
  const [cases, setCases] = useState([]);
  const [outcomes, setOutcomes] = useState(null);
  const [form, setForm] = useState({ diagnosis: "", complexity: "moderate", question: "" });

  const load = () => api.get("/peer-consult/cases").then(r=>setCases(r.data));
  useEffect(() => { load(); api.get("/peer-consult/outcomes").then(r=>setOutcomes(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/peer-consult/cases", form);
    toast.success("Case submitted to peer network");
    setForm({ diagnosis: "", complexity: "moderate", question: "" });
    load();
  };

  const cols = [
    { key: "diagnosis", label: "Diagnosis" },
    { key: "complexity", label: "Complexity" },
    { key: "question", label: "Question" },
    { key: "matched_specialist", label: "Matched Specialist" },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 19" title="Peer Consult Network"
        subtitle="Anonymous second-opinion submission, AI-matched specialist routing, MDT preparation, outcome tracking." testid="peer-page" />

      {outcomes && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={UserCog} label="Total cases" value={outcomes.total_cases} testid="kpi-cases" />
          <KpiCard label="Diagnosis changed" value={outcomes.diagnosis_changed} hint="Material impact" testid="kpi-dx" />
          <KpiCard label="Treatment changed" value={outcomes.treatment_changed} testid="kpi-tx" />
          <KpiCard label="Avg response" value={`${outcomes.avg_response_hours}h`} accent testid="kpi-resp" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Submit Case (anonymous)" testid="peer-form">
          <form onSubmit={submit} className="space-y-2">
            <Icd10Autocomplete
              value={form.diagnosis}
              onChange={(v)=>setForm({...form, diagnosis: v})}
              placeholder="Diagnosis — ICD-10 code or keyword"
              testid="peer-dx-icd"
              single
            />
            <input placeholder="Diagnosis (free text fallback)" value={form.diagnosis} onChange={(e)=>setForm({...form, diagnosis:e.target.value})} required className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm mt-2" data-testid="peer-dx" />
            <select value={form.complexity} onChange={(e)=>setForm({...form, complexity:e.target.value})} className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option value="low">Low complexity</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
            <textarea placeholder="Specific clinical question…" value={form.question} onChange={(e)=>setForm({...form, question:e.target.value})} rows={4} required className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="peer-q" />
            <button type="submit" className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Submit</button>
          </form>
        </SectionCard>
        <div className="lg:col-span-2">
          <SectionCard title="Active Cases" testid="peer-cases">
            <DataTable data={cases} columns={cols} searchKeys={["diagnosis","matched_specialist","status"]} testid="peer-table" />
          </SectionCard>
        </div>
      </div>
    </>
  );
}
